import React from 'react'
import useStore from 'reusable/mobx-store-utils/useStore'
import { observer } from 'mobx-react-lite'
import { DataGrid, DataGridProps, GridRowHeightParams, GridRowHeightReturnValue, GridRowIdGetter, GridValidRowModel } from '@mui/x-data-grid'
import GapDataGridNoRows from './GapDataGridNoRows'
import GapDataGridStore, { GapDataGridStoreProvider } from './GapDataGridStore'
import { useGridApiRef, GridColDef, GridEventListener } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import GapDataGridToolbar from './toolbar/GapDataGridToolbar'
import { useThemeStore } from 'theme/ThemeStore'
import GapAlert from '../alert/GapAlert'

const getRowHeight = (params: GridRowHeightParams): GridRowHeightReturnValue => {
   return 'auto'
}

export type GapDataGridRowClick<T> = Omit<GridEventListener<"rowClick">, "row"> & { row: T }

export type GapDataGridFilter<T> = {
   [K in keyof T]: {
      id?: string
      field: K
      operator: 'contains' | 'doesNotContain' | 'equals' | "doesNotEqual" | "startsWith" | "endsWith" | "isEmpty" | "isNotEmpty" | "isAnyOf"
      value: T[K] | T[K][]
   }
}[keyof T]

/** IMPORTANT. If the filters dont work, ensure that the field is set in the columns. Use hide: true to keep the column registered (so filters/sorting work) without rendering it. */
export type GapDataGridAdditionalFilter<_T> = {
   name: string
   /** Selected if none other is selcted. Cannot be unselected */
   default?: boolean
   onClick: () => void
}

export interface GapDataGridToolbarProps<T> {
   /** Button properties when rows are selected and checkboxSelection is enabled */
   onSelection?: {
      /** callback function with selectedRows */
      callback: (ids: string[]) => void
      /** Return button text based on the amount of rows selected */
      buttonLabel: (rowCount: number) => string
      /** Only show this if we are on this tab, by label */
      onlyForTab?: string
      /** Only show this for a given archive mode. false => only when viewing active rows, true => only when viewing archived rows */
      onlyForArchiveMode?: boolean
   }[]
   startAdornment?: React.ReactNode
   tabs?: {
      label: string
      noRowsText?: string
      /** Shown in place of noRowsText when the archive toggle is enabled and this tab has no archived rows. */
      noArchivedRowsText?: string
      filters: GapDataGridFilter<T>[]
      /** Fired when this tab becomes active (on initial mount and on every tab change). */
      onClick?: () => void
   }[],
   /**
    * Label of the tab to show. Pair it with `onTabChange` to drive the active tab from the consumer's own state (e.g.
    * to land on a particular tab after an action); omit both and the grid tracks the tab itself.
    */
   selectedTabLabel?: string
   /** Fired with the label the user switched to. Needed for `selectedTabLabel` to keep up with manual switches. */
   onTabChange?: (label: string) => void
   additionalFilters?: GapDataGridAdditionalFilter<T>[]
   /** Adds an archive toggle button to the right of the search field. When enabled, only archived rows are shown; when disabled, only active rows. */
   archive?: {
      /** Returns true if the row is archived. Used to pre-filter the rows when archive mode is toggled. */
      isArchived: (row: T) => boolean
      /** Noun phrase describing the archived rows, used in the "Viewing archived {label}" banner. */
      label: string
   }
}

export type GapDataGridColumns<T extends GridValidRowModel = any> = (GridColDef<T> & { tabFilters?: string[]; hide?: boolean })[]

export interface GapDataGridProps<T extends GridValidRowModel = any> {
   /** Your columns. Some defaults may apply */
   columns: GapDataGridColumns<T>
   /** Your data */
   rows: T[]
   /** Loading state of the DataGrid */
   loading?: boolean | undefined
   /** Default on row click handler */
   onRowClick?: GridEventListener<"rowClick"> | undefined
   /** Default on cell click handler */
   onCellClick?: GridEventListener<"cellClick"> | undefined
   /** If not specified it will use system wide default of lastUpdated */
   sortBy?: string
   /** Sort direction. Defaults to 'desc' for lastUpdatedAt, 'asc' for custom fields */
   sortDirection?: 'asc' | 'desc'
   /** This is the text that shows in the middle when no rows exists */
   noRowsText?: string,
   /** If true, the grid get a first column with a checkbox that allows to select rows. */
   checkboxSelection?: boolean
   /** Extra sx props you'd want to pass through */
   extraSx?: DataGridProps['sx']
   getRowId?: GridRowIdGetter<any> | undefined
   toolbar?: GapDataGridToolbarProps<T>
   defaultPageSize?: number
   hideToolbar?: boolean
   /** Grow the grid to fit its rows instead of filling the parent. Required when placed inside a layout that measures content height (e.g. AnimateHeight). */
   autoHeight?: boolean
}

export default observer(function GapDataGrid<T extends GridValidRowModel>(props: GapDataGridProps<T>) {
   const gridApiRef = useGridApiRef()
   const themeStore = useThemeStore()
   const gapDataGridStore = useStore(sp => new GapDataGridStore<T>(sp), { ...props, gridApiRef })

   return <GapDataGridStoreProvider store={gapDataGridStore}>
      <Box sx={props.autoHeight ? { width: '100%', minWidth: 0 } : { flexGrow: 1, position: 'relative' }}>
         <Box
            sx={
               props.autoHeight
                  ? { display: 'flex', flexDirection: 'column', gap: 2, width: '100%', minWidth: 0, maxWidth: '100%', minHeight: gapDataGridStore.displayRows.length === 0 ? '300px' : 'inherit' }
                  : { display: 'flex', flexDirection: 'column', gap: 2, position: 'absolute', maxWidth: '100%', width: '100%', minHeight: gapDataGridStore.displayRows.length === 0 ? '300px' : 'inherit' }
            }
         >
            {!props.hideToolbar && <GapDataGridToolbar<T> />}
            {props.toolbar?.archive && gapDataGridStore.archiveMode &&
               <GapAlert severity="info">Viewing archived {props.toolbar.archive.label}</GapAlert>
            }
            <DataGrid
               autoHeight={props.autoHeight}
               onRowSelectionModelChange={gapDataGridStore.onRowSelectionModelChange}
               checkboxSelection={props.checkboxSelection}
               disableRowSelectionOnClick
               columns={gapDataGridStore.columns}
               rows={gapDataGridStore.displayRows}
               loading={props.loading}
               onRowClick={props.onRowClick}
               onCellClick={props.onCellClick}
               apiRef={gridApiRef}
               getRowId={props.getRowId}
               initialState={{
                  sorting: {
                     sortModel: [{
                        field: props.sortBy || 'lastUpdatedAt',
                        sort: props.sortDirection || (props.sortBy ? 'asc' : 'desc')
                     }],
                  },
                  pagination: { paginationModel: { pageSize: props.defaultPageSize || 10 } },
                  columns: {
                     columnVisibilityModel: Object.fromEntries(
                        props.columns.filter(c => c.hide).map(c => [c.field, false])
                     ),
                  },
               }}

               getRowHeight={getRowHeight}

               columnHeaderHeight={40}
               paginationMode="client"
               pageSizeOptions={[5, 10, 20]}
               pagination

               slots={{
                  noResultsOverlay: GapDataGridNoRows,
                  noRowsOverlay: GapDataGridNoRows,
               }}

               disableColumnMenu
               disableColumnFilter
               disableColumnSelector

               sx={{
                  minHeight: gapDataGridStore.displayRows.length === 0 ? '300px' : 'inherit',
                  backgroundColor: themeStore.backgroundImageUrl ? 'transparent' : themeStore.panelColors.panelBackground,
                  /** Hide colum seperator */
                  "& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator": {
                     display: "none"
                  },
                  /** Hide the hidious outline around a header when selected and set correct background */
                  "& .MuiDataGrid-columnHeaders": {
                     overflow: 'hidden',
                     borderRadius: '18px 18px 0px 0px',
                  },
                  "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
                     outline: "none"
                  },
                  // Hide borders around cells
                  "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                     outline: "none"
                  },
                  /** Hide sort buttons if no rows */
                  "& .MuiDataGrid-iconButtonContainer": {
                     display: gapDataGridStore.displayRows.length ? 'inherit' : 'none'
                  },
                  ...(props.onRowClick || props.onCellClick ? {
                     '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer'
                     }
                  } : {}),
                  "& .MuiDataGrid-cell": {
                     display: "flex", // Makes content align flexibly
                     alignItems: "center", // Centers content vertically
                     padding: "12px", // Adds padding inside each cell
                  },
                  borderRadius: '1.3em',
                  '& > div > div': {
                     bottom: 0,
                  },
                  ...props.extraSx
               }}
            />
         </Box>
      </Box>
   </GapDataGridStoreProvider>
}) as <T extends GridValidRowModel>(props: GapDataGridProps<T>) => JSX.Element
